import { Package, Plus, Edit2, Trash2, Loader2, DollarSign, Calendar, Users, ArrowLeft } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { usePermissions } from '../../hooks/usePermissions';
import { getCurrentOnboardingSection } from '../../utils/onboardingCheck';
import type { Database } from '../../types/supabase';
import { Dropdown } from '../../components/ui/Dropdown';

interface Subscription {
  id: string;
  name: string;
  price: number;
  subscription_type: string;
  access_type: string;
  access_value: number;
  contract_term: string;
  billing_cycle: string;
  prepayment_required: boolean;
  prepayment_months: number;
  is_free_trial: boolean;
  requires_approval: boolean;
  allows_cash_payment: boolean;
  trial_duration_days: number | null;
  trial_duration_classes: number | null;
  trial_duration_period: string | null;
  created_at: string;
  sports_included: string[];
  amenities_included: string[];
}

export default function Subscriptions() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  
  // Use current company context hook instead of hardcoded limit(1)
  const { data: companyId, role, isLoading: isLoadingCompany } = useCurrentCompany();
  const permissions = usePermissions();
  
  // Check if user is in onboarding flow
  const currentOnboardingSection = getCurrentOnboardingSection();
  const isInOnboarding = currentOnboardingSection === 'subscriptions';

  useEffect(() => {
    const loadData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/signin');
        return;
      }

      // Wait for company context to load
      if (isLoadingCompany) {
        return;
      }

      // Wait for permissions to load
      if (permissions.isLoading) {
        return;
      }

      // Check if user has a company context
      if (!companyId) {
        // User doesn't have access to any company or needs to select one
        navigate('/company-selector');
        return;
      }

      // Verify user has permission to view subscriptions (aligns with route guard STAFF roles)
      // Route guard already ensures staff access; use role to avoid permission-loading race
      const hasViewAccess = ['owner', 'admin', 'coach', 'reception'].includes(role || '');
      if (!hasViewAccess) {
        navigate('/dashboard');
        return;
      }

      // Load subscriptions for the current company context
      const { data: subs, error } = await supabase
        .from('company_subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading subscriptions:', error);
        setLoading(false);
        return;
      }

      setSubscriptions(subs || []);
      setLoading(false);
    };

    loadData();
  }, [navigate, companyId, role, isLoadingCompany, permissions.isLoading, permissions.canViewAccountRecords, permissions.isAdmin, permissions.isDeveloper]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) return;

    const { error } = await supabase
      .from('company_subscriptions')
      .delete()
      .eq('id', id);

    if (error) {
      alert('Failed to delete subscription');
      return;
    }

    setSubscriptions(prev => prev.filter(s => s.id !== id));
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'recurring': return 'Recurring';
      case 'punch_card': return 'Punch Card';
      case 'trial': return 'Trial';
      case 'drop_in': return 'Drop-In';
      default: return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'recurring': return 'bg-green-600/20 text-green-500';
      case 'punch_card': return 'bg-blue-600/20 text-blue-500';
      case 'trial': return 'bg-yellow-600/20 text-yellow-500';
      case 'drop_in': return 'bg-purple-600/20 text-purple-500';
      default: return 'bg-gray-600/20 text-gray-500';
    }
  };

  const getAccessLabel = (sub: Subscription) => {
    if (sub.access_type === 'unlimited') return 'Unlimited';
    if (sub.access_type === 'days') return `${sub.access_value} days/month`;
    if (sub.access_type === 'classes') return `${sub.access_value} classes/month`;
    return sub.access_type;
  };

  if (loading || isLoadingCompany || permissions.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] w-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#9B1421]" />
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] w-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-[#EBFFEE] mb-2">No Company Selected</h2>
          <p className="text-[#A9A3A3] mb-4">Please select a company to manage subscriptions.</p>
          <button
            onClick={() => navigate('/company-selector')}
            className="px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
          >
            Select Company
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-5rem)] flex flex-col pt-6">
        {/* Onboarding Banner */}
        {isInOnboarding && (
          <div className="mb-4 bg-[#9B1421]/10 border border-[#9B1421]/30 rounded-lg p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <Package size={20} className="text-text-primary" />
              <div>
                <p className="text-sm font-medium text-[#EBFFEE]">Setting up your gym</p>
                <p className="text-xs text-[#A9A3A3]">Create subscriptions to continue onboarding</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/onboarding')}
              className="flex items-center gap-2 px-4 py-2 bg-[#504A4A] hover:bg-[#5A5454] text-white font-medium rounded-lg transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              Back to Onboarding
            </button>
          </div>
        )}

        {/* Add Subscription - compact top-right when user can manage and has subscriptions */}
        {permissions.canManageSubscriptions && subscriptions.length > 0 && (
          <div className="flex justify-end mb-4 shrink-0">
            <button
              onClick={() => {
                setEditingSubscription(null);
                setShowCreateForm(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
            >
              <Plus size={18} />
              Add Subscription
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-h-0">
        {subscriptions.length === 0 && !showCreateForm ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-16">
            <div className="w-20 h-20 rounded-full bg-[#363232] flex items-center justify-center mx-auto mb-4">
              <Package size={40} className="text-[#504A4A]" />
            </div>
            <h2 className="text-xl font-semibold text-[#EBFFEE] mb-2">No Subscriptions Yet</h2>
            <p className="text-[#A9A3A3] mb-6">Create your first subscription plan for members</p>
            {/* Only show create button if user can manage subscriptions */}
            {permissions.canManageSubscriptions && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
              >
                <Plus size={18} />
                Create First Subscription
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {subscriptions.map((sub) => (
              <div
                key={sub.id}
                className="bg-[#221F1F] rounded-xl border border-[#504A4A] p-5 hover:border-[#9B1421]/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-[#EBFFEE] text-lg">{sub.name}</h3>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 ${getTypeColor(sub.subscription_type)}`}>
                      {getTypeLabel(sub.subscription_type)}
                    </span>
                  </div>
                  {/* Only show edit/delete buttons if user can manage subscriptions */}
                  {permissions.canManageSubscriptions && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          setEditingSubscription(sub);
                          setShowCreateForm(true);
                        }}
                        className="p-2 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#504A4A] rounded-lg transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(sub.id)}
                        className="p-2 text-[#A9A3A3] hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <DollarSign size={16} className="text-rollops-accent" />
                    <span className="text-[#EBFFEE] font-medium">
                      ${sub.price.toFixed(2)}
                      {sub.subscription_type === 'recurring' && (
                        <span className="text-[#A9A3A3] font-normal text-sm">
                          /{sub.billing_cycle === 'monthly' ? 'mo' : sub.billing_cycle}
                        </span>
                      )}
                    </span>
                    {sub.is_free_trial && (
                      <span className="text-xs bg-green-600/20 text-green-500 px-2 py-0.5 rounded-full">Free</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-[#A9A3A3]" />
                    <span className="text-sm text-[#A9A3A3]">{getAccessLabel(sub)}</span>
                  </div>

                  {sub.subscription_type === 'recurring' && (
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-[#A9A3A3]" />
                      <span className="text-sm text-[#A9A3A3]">
                        {sub.contract_term === 'monthly' ? 'Monthly' : 
                         sub.contract_term === 'quarterly' ? 'Quarterly' :
                         sub.contract_term === 'semi_annual' ? 'Semi-Annual' :
                         sub.contract_term === 'annual' ? 'Annual' : sub.contract_term} contract
                      </span>
                    </div>
                  )}

                  {sub.allows_cash_payment && (
                    <span className="inline-block text-xs bg-[#504A4A] text-[#A9A3A3] px-2 py-0.5 rounded-full">
                      Cash accepted
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

          {/* Create/Edit Form Modal - Only show if user can manage subscriptions */}
          {showCreateForm && companyId && permissions.canManageSubscriptions && (
            <SubscriptionForm
              companyId={companyId}
              subscription={editingSubscription}
              onClose={() => {
                setShowCreateForm(false);
                setEditingSubscription(null);
              }}
              onSave={(sub) => {
                if (editingSubscription) {
                  setSubscriptions(prev => prev.map(s => s.id === sub.id ? sub : s));
                } else {
                  setSubscriptions(prev => [sub, ...prev]);
                }
                setShowCreateForm(false);
                setEditingSubscription(null);
              }}
            />
          )}
        </div>
      </div>
  );
}

// Subscription Form Component
interface SubscriptionFormProps {
  companyId: string;
  subscription: Subscription | null;
  onClose: () => void;
  onSave: (subscription: Subscription) => void;
}

function SubscriptionForm({ companyId, subscription, onClose, onSave }: SubscriptionFormProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: subscription?.name || '',
    price: subscription?.price?.toString() || '',
    subscriptionType: subscription?.subscription_type || 'recurring',
    accessType: subscription?.access_type || 'unlimited',
    accessValue: subscription?.access_value?.toString() || '',
    contractTerm: subscription?.contract_term || 'monthly',
    billingCycle: subscription?.billing_cycle || 'monthly',
    prepaymentRequired: subscription?.prepayment_required || false,
    prepaymentMonths: subscription?.prepayment_months?.toString() || '',
    isFreeTrial: subscription?.is_free_trial || false,
    requiresApproval: subscription?.requires_approval ?? true,
    allowsCashPayment: subscription?.allows_cash_payment || false,
    // Trial duration fields
    trialDurationType: subscription?.trial_duration_days ? 'days' : 
                       subscription?.trial_duration_classes ? 'classes' : 
                       subscription?.trial_duration_period ? 'period' : 'days',
    trialDurationDays: subscription?.trial_duration_days?.toString() || '',
    trialDurationClasses: subscription?.trial_duration_classes?.toString() || '',
    trialDurationPeriod: subscription?.trial_duration_period || '30 days',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Subscription name is required');
      return;
    }
    if (!form.price && !form.isFreeTrial) {
      setError('Price is required');
      return;
    }
    if (form.prepaymentRequired && (!form.prepaymentMonths || parseInt(form.prepaymentMonths) <= 0)) {
      setError('Pre-payment months must be greater than 0 when pre-payment is required');
      return;
    }
    // Validate trial duration
    if (form.subscriptionType === 'trial') {
      const hasDays = form.trialDurationType === 'days' && form.trialDurationDays && parseInt(form.trialDurationDays) > 0;
      const hasClasses = form.trialDurationType === 'classes' && form.trialDurationClasses && parseInt(form.trialDurationClasses) > 0;
      const hasPeriod = form.trialDurationType === 'period' && form.trialDurationPeriod;
      if (!hasDays && !hasClasses && !hasPeriod) {
        setError('Please specify trial duration (days, classes, or period)');
        return;
      }
    }

    setSaving(true);
    setError(null);

    const data: Partial<Database['public']['Tables']['company_subscriptions']['Insert']> = {
      company_id: companyId,
      name: form.name.trim(),
      price: form.isFreeTrial ? 0 : parseFloat(form.price) || 0,
      subscription_type: form.subscriptionType,
      access_type: form.accessType,
      access_value: form.accessType === 'unlimited' ? 0 : parseInt(form.accessValue) || 0,
      contract_term: form.contractTerm,
      billing_cycle: form.billingCycle,
      prepayment_required: form.prepaymentRequired,
      prepayment_months: form.prepaymentRequired ? parseInt(form.prepaymentMonths) || 0 : 0,
      is_free_trial: form.isFreeTrial,
      requires_approval: form.requiresApproval,
      allows_cash_payment: form.allowsCashPayment,
      sports_included: [],
      amenities_included: [],
    };

    // Set trial duration based on type
    if (form.subscriptionType === 'trial') {
      if (form.trialDurationType === 'days') {
        data.trial_duration_days = parseInt(form.trialDurationDays) || null;
        data.trial_duration_classes = null;
        data.trial_duration_period = null;
      } else if (form.trialDurationType === 'classes') {
        data.trial_duration_classes = parseInt(form.trialDurationClasses) || null;
        data.trial_duration_days = null;
        data.trial_duration_period = null;
      } else if (form.trialDurationType === 'period') {
        data.trial_duration_period = form.trialDurationPeriod || null;
        data.trial_duration_days = null;
        data.trial_duration_classes = null;
      }
    } else {
      // Clear trial duration fields for non-trial subscriptions
      data.trial_duration_days = null;
      data.trial_duration_classes = null;
      data.trial_duration_period = null;
    }

    try {
      if (subscription) {
        // Update
        const { data: updated, error: updateError } = await supabase
          .from('company_subscriptions')
          .update(data)
          .eq('id', subscription.id)
          .select()
          .single();

        if (updateError) throw updateError;
        onSave(updated);
      } else {
        // Create
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: created, error: createError } = await (supabase.from as any)('company_subscriptions')
          .insert(data)
          .select()
          .single();

        if (createError) throw createError;
        onSave(created);
      }
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#221F1F] rounded-2xl border border-[#504A4A] w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#504A4A]">
          <h2 className="text-xl font-bold text-[#EBFFEE]">
            {subscription ? 'Edit Subscription' : 'Create Subscription'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Subscription Name <span className="text-rollops-accent">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Basic Monthly Plan"
              maxLength={100}
              className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              required
            />
          </div>

          {/* Subscription Type */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Subscription Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['recurring', 'punch_card', 'trial', 'drop_in'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setForm({ ...form, subscriptionType: type })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.subscriptionType === type
                      ? 'bg-[#9B1421] text-white'
                      : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                  }`}
                >
                  {type === 'recurring' ? 'Recurring' :
                   type === 'punch_card' ? 'Punch Card' :
                   type === 'trial' ? 'Trial' : 'Drop-In'}
                </button>
              ))}
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Price (USD)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A9A3A3]">$</span>
              <input
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="99.00"
                disabled={form.isFreeTrial}
                className="w-full pl-8 pr-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50 disabled:opacity-50"
              />
            </div>
            {form.subscriptionType === 'trial' && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isFreeTrial}
                  onChange={(e) => setForm({ ...form, isFreeTrial: e.target.checked })}
                  className="w-4 h-4 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
                />
                <span className="text-sm text-[#A9A3A3]">Free trial</span>
              </label>
            )}
          </div>

          {/* Access Level */}
          <div>
            <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
              Access Level
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, accessType: 'days' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.accessType === 'days'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Days
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, accessType: 'classes' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.accessType === 'classes'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Classes
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, accessType: 'unlimited', accessValue: '' })}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  form.accessType === 'unlimited'
                    ? 'bg-[#9B1421] text-white'
                    : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                }`}
              >
                Unlimited
              </button>
            </div>
            {form.accessType !== 'unlimited' && (
              <input
                type="number"
                value={form.accessValue}
                onChange={(e) => setForm({ ...form, accessValue: e.target.value })}
                placeholder={`Number of ${form.accessType}`}
                className="w-full mt-2 px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              />
            )}
          </div>

          {/* Trial Duration (Trial subscriptions only) */}
          {form.subscriptionType === 'trial' && (
            <div>
              <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                Trial Duration Type
              </label>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, trialDurationType: 'days' })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.trialDurationType === 'days'
                      ? 'bg-[#9B1421] text-white'
                      : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                  }`}
                >
                  Days
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, trialDurationType: 'classes' })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.trialDurationType === 'classes'
                      ? 'bg-[#9B1421] text-white'
                      : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                  }`}
                >
                  Classes
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, trialDurationType: 'period' })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    form.trialDurationType === 'period'
                      ? 'bg-[#9B1421] text-white'
                      : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                  }`}
                >
                  Period
                </button>
              </div>

              {form.trialDurationType === 'days' && (
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Number of Days
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.trialDurationDays}
                    onChange={(e) => setForm({ ...form, trialDurationDays: e.target.value })}
                    placeholder="e.g., 30"
                    className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                  />
                </div>
              )}

              {form.trialDurationType === 'classes' && (
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Number of Classes
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.trialDurationClasses}
                    onChange={(e) => setForm({ ...form, trialDurationClasses: e.target.value })}
                    placeholder="e.g., 5"
                    className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                  />
                </div>
              )}

              {form.trialDurationType === 'period' && (
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Trial Period
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {['7 days', '14 days', '30 days', '60 days', '90 days'].map((period) => (
                      <button
                        key={period}
                        type="button"
                        onClick={() => setForm({ ...form, trialDurationPeriod: period })}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          form.trialDurationPeriod === period
                            ? 'bg-[#9B1421] text-white'
                            : 'bg-[#504A4A] text-[#A9A3A3] hover:bg-[#5A5454]'
                        }`}
                      >
                        {period}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contract Term (Recurring only) */}
          {form.subscriptionType === 'recurring' && (
            <>
              <Dropdown
                label="Contract Term"
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'quarterly', label: 'Quarterly' },
                  { value: 'semi_annual', label: 'Semi-Annual' },
                  { value: 'annual', label: 'Annual' },
                ]}
                value={form.contractTerm}
                onChange={(value: string) => setForm({ ...form, contractTerm: value })}
              />
              <Dropdown
                label="Billing Cycle"
                options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'quarterly', label: 'Quarterly' },
                  { value: 'annual', label: 'Annual' },
                ]}
                value={form.billingCycle}
                onChange={(value: string) => setForm({ ...form, billingCycle: value })}
              />
            </>
          )}

          {/* Prepayment (Recurring only) */}
          {form.subscriptionType === 'recurring' && (
            <div>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={form.prepaymentRequired}
                  onChange={(e) => setForm({ ...form, prepaymentRequired: e.target.checked, prepaymentMonths: e.target.checked ? form.prepaymentMonths : '' })}
                  className="w-5 h-5 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
                />
                <span className="text-sm text-[#EBFFEE]">Require Pre-payment</span>
              </label>
              {form.prepaymentRequired && (
                <div className="ml-8">
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Pre-payment Months (0-24)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="24"
                    value={form.prepaymentMonths}
                    onChange={(e) => {
                      const value = e.target.value;
                      const num = parseInt(value, 10);
                      if (value === '' || (!isNaN(num) && num >= 0 && num <= 24)) {
                        setForm({ ...form, prepaymentMonths: value });
                      }
                    }}
                    placeholder="e.g., 3"
                    className="w-full px-4 py-3 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
                  />
                </div>
              )}
            </div>
          )}

          {/* Options */}
          <div className="space-y-3">
            {form.subscriptionType === 'trial' && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.requiresApproval}
                  onChange={(e) => setForm({ ...form, requiresApproval: e.target.checked })}
                  className="w-5 h-5 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
                />
                <span className="text-sm text-[#EBFFEE]">Requires admin approval</span>
              </label>
            )}

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.allowsCashPayment}
                onChange={(e) => setForm({ ...form, allowsCashPayment: e.target.checked })}
                className="w-5 h-5 rounded border-[#504A4A] bg-[#504A4A] text-rollops-accent focus:ring-[#9B1421]"
              />
              <span className="text-sm text-[#EBFFEE]">Allow cash payment</span>
            </label>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] disabled:bg-[#504A4A] disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Saving...
                </>
              ) : (
                subscription ? 'Update' : 'Create'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

